// Tiny helper for cover images stored in Supabase Storage.
// Adds explicit width/height/sizes attrs so browsers can reserve space
// (no CLS) and pick the right intrinsic size — without requiring the
// Supabase image-transformation add-on.

export const COVER_ASPECT = 16 / 10;

/**
 * Build a Supabase Storage transform URL at a given width.
 * Works against URLs of the form `<host>/storage/v1/object/public/<bucket>/<path>`
 * by rewriting them to `<host>/storage/v1/render/image/public/<bucket>/<path>?width=&quality=`.
 * Returns null if the URL is not a Supabase storage public URL.
 */
function supabaseRenderUrl(
  _src: string,
  _width: number,
  _quality = 78,
  _format: "webp" | "avif" | "origin" = "webp",
): string | null {
  // Supabase image transformations not enabled on this project.
  return null;
}

/**
 * Build a responsive `srcset` for a Supabase-hosted cover image.
 * Returns undefined for non-Supabase URLs so callers can skip the attribute.
 */
export function coverSrcSet(
  src: string | null | undefined,
  widths = [640, 960, 1280, 1600],
): string | undefined {
  if (!src) return undefined;
  const parts = widths
    .map((w) => {
      const url = supabaseRenderUrl(src, w);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : undefined;
}

/**
 * Returns sensible <img> attrs for a Supabase blog cover.
 * `width` is the intended display width in CSS px (used to compute height
 * via the 16:10 cover aspect). Pass `eager: true` for above-the-fold images.
 */
export function coverImgAttrs(opts: { width: number; eager?: boolean; sizes?: string }): {
  width: number;
  height: number;
  loading: "eager" | "lazy";
  decoding: "async";
  fetchPriority: "high" | "low" | "auto";
  sizes?: string;
} {
  const height = Math.round(opts.width / COVER_ASPECT);
  return {
    width: opts.width,
    height,
    loading: opts.eager ? "eager" : "lazy",
    decoding: "async",
    fetchPriority: opts.eager ? "high" : "auto",
    sizes: opts.sizes,
  };
}
